import { useState, useEffect } from 'react'
import { listUserFiles } from '@/services/api'
import styles from './FileList.module.css'

export default function FileList({ autoRefresh = false, folderFilter = null }) {
    const [files, setFiles] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const loadFiles = async () => {
        try {
            setLoading(true)
            setError(null)
            const data = await listUserFiles(30)

            // Filtrar archivos por carpeta si se especifica
            let filteredFiles = data.files || []
            if (folderFilter) {
                filteredFiles = filteredFiles.filter(file =>
                    file.key && file.key.includes(`/${folderFilter}/`)
                )
            }

            setFiles(filteredFiles)
        } catch (err) {
            console.error('Error cargando archivos:', err)
            setError('No se pudieron cargar los archivos')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadFiles()

        // Auto-refresh si está habilitado
        if (autoRefresh) {
            const interval = setInterval(loadFiles, 30000) // 30 segundos
            return () => clearInterval(interval)
        }
    }, [autoRefresh, folderFilter])

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B'
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    }

    const formatDate = (isoString) => {
        const date = new Date(isoString)
        const now = new Date()
        const diffMs = now - date
        const diffMins = Math.floor(diffMs / 60000)

        if (diffMins < 1) return 'Hace un momento'
        if (diffMins < 60) return `Hace ${diffMins} minutos`

        const diffHours = Math.floor(diffMins / 60)
        if (diffHours < 24) return `Hace ${diffHours} horas`

        const diffDays = Math.floor(diffHours / 24)
        if (diffDays < 7) return `Hace ${diffDays} días`

        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        })
    }

    if (loading && files.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>Cargando archivos...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.error}>
                    <p>⚠️ {error}</p>
                    <button onClick={loadFiles} className={styles.retryBtn}>
                        Reintentar
                    </button>
                </div>
            </div>
        )
    }

    if (files.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.empty}>
                    <div className={styles.emptyIcon}>📂</div>
                    <p className={styles.emptyTitle}>No hay archivos procesados</p>
                    <p className={styles.emptyText}>
                        Los archivos procesados aparecerán aquí después de completar una comparación
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h3 className={styles.title}>Archivos Procesados ({files.length})</h3>
                <button
                    onClick={loadFiles}
                    className={styles.refreshBtn}
                    disabled={loading}
                >
                    {loading ? '⟳' : '🔄'} Actualizar
                </button>
            </div>

            <div className={styles.fileList}>
                {files.map((file, index) => (
                    <div key={index} className={styles.fileItem}>
                        <div className={styles.fileIcon}>📄</div>
                        <div className={styles.fileInfo}>
                            <div className={styles.fileName}>{file.name}</div>
                            <div className={styles.fileMeta}>
                                <span className={styles.fileDate}>
                                    {formatDate(file.lastModified)}
                                </span>
                                <span className={styles.fileSeparator}>•</span>
                                <span className={styles.fileSize}>
                                    {formatFileSize(file.size)}
                                </span>
                            </div>
                        </div>
                        <a
                            href={file.downloadUrl}
                            download={file.name}
                            className={styles.downloadBtn}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span className={styles.downloadIcon}>⬇️</span>
                            Descargar
                        </a>
                    </div>
                ))}
            </div>
        </div>
    )
}
